import { api } from './client';
import type {
  Admin,
  AdminBookingDetail,
  AdminBookingRow,
  Aircraft,
  AircraftCategory,
  Airport,
  AirportHit,
  AirportPage,
  AirportStatus,
  AppSettings,
  BookingCounts,
  BookingStatus,
  BookingWhen,
  CharterBookingDetail,
  CharterBookingList,
  CharterBookingStatus,
  CharterQuoteBreakdown,
  CharterRequestDetail,
  CharterRequestList,
  CharterRequestStatus,
  Operator,
  Paginated,
  Status,
} from './types';
import type {
  AirportCreateBody,
  AirportUpdateBody,
  BookingPassengerAddBody,
  BookingPassengerUpdateBody,
  BookingUpdateBody,
  CharterBookingUpdateBody,
  CharterPassengerAddBody,
  CharterPassengerUpdateBody,
  CharterQuoteBody,
  CharterQuotePreviewBody,
  CharterRequestUpdateBody,
} from './schemas';

// ─── Auth ──────────────────────────────────────────────────────────────
export async function login(email: string, password: string) {
  const { data } = await api.post<{ token: string; admin: Admin }>('/auth/login', {
    email,
    password,
  });
  return data;
}
export async function me() {
  const { data } = await api.get<Admin>('/auth/me');
  return data;
}

// ─── Operators ─────────────────────────────────────────────────────────
export async function listOperators(p: {
  q?: string;
  status?: Status;
  page?: number;
  pageSize?: number;
}) {
  const { data } = await api.get<Paginated<Operator>>('/operators', { params: p });
  return data;
}
export async function createOperator(body: Partial<Operator>) {
  const { data } = await api.post<Operator>('/operators', body);
  return data;
}
export async function updateOperator(id: string, body: Partial<Operator>) {
  const { data } = await api.patch<Operator>(`/operators/${id}`, body);
  return data;
}
export async function deleteOperator(id: string) {
  await api.delete(`/operators/${id}`);
}

// ─── Aircraft ──────────────────────────────────────────────────────────
export async function listAircraft(p: {
  tail?: string;
  category?: AircraftCategory;
  operatorId?: string;
  status?: Status;
  page?: number;
  pageSize?: number;
}) {
  const { data } = await api.get<Paginated<Aircraft>>('/aircraft', { params: p });
  return data;
}
export async function getAircraft(id: string) {
  const { data } = await api.get<Aircraft>(`/aircraft/${id}`);
  return data;
}
export async function createAircraft(body: Partial<Aircraft>) {
  const { data } = await api.post<Aircraft>('/aircraft', body);
  return data;
}
export async function updateAircraft(id: string, body: Partial<Aircraft>) {
  const { data } = await api.patch<Aircraft>(`/aircraft/${id}`, body);
  return data;
}
export async function deleteAircraft(id: string) {
  await api.delete(`/aircraft/${id}`);
}

// ─── Bookings (SHAR-46 / SHAR-27 / SHAR-62) ────────────────────────────
/** SHAR-61 — the values the Bookings list's dropdown filters offer. */
export type BookingFilterOptions = {
  operators: { id: string; name: string }[];
  models: string[];
  tails: string[];
  airports: { code: string; label: string; iata: string | null; icao: string | null }[];
};
// SHAR-1079..1082 — the options depend on the view and on the other filters.
export async function getBookingFilterOptions(p: {
  when?: BookingWhen;
  from?: string;
  to?: string;
  q?: string;
  operatorId?: string;
  model?: string;
  tail?: string;
  airport?: string;
} = {}) {
  const { data } = await api.get<BookingFilterOptions>('/admin/bookings/filter-options', { params: p });
  return data;
}

export async function listBookings(p: {
  status?: BookingStatus;
  q?: string;
  page?: number;
  pageSize?: number;
  when?: BookingWhen;
  mgavOverdue?: boolean;
  adhocNewSeats?: boolean;
  notPaid?: boolean;
  passportNeeded?: boolean; // SHAR-451
  /** SHAR-1038 — departure date range, "yyyy-mm-dd", inclusive. */
  from?: string;
  to?: string;
  /** SHAR-61 — the dropdown filters; `airport` is the stored (IATA) code. */
  operatorId?: string;
  model?: string;
  tail?: string;
  airport?: string;
}) {
  const { data } = await api.get<{
    data: AdminBookingRow[];
    total: number;
    page: number;
    pageSize: number;
  }>('/admin/bookings', {
    params: {
      ...p,
      mgavOverdue: p.mgavOverdue ? 'true' : undefined,
      adhocNewSeats: p.adhocNewSeats ? 'true' : undefined,
      notPaid: p.notPaid ? 'true' : undefined,
      passportNeeded: p.passportNeeded ? 'true' : undefined, // SHAR-451
    },
  });
  return data;
}
// SHAR-1072 — the same filters as listBookings, minus the status chip and the
// label cards, so the counters count the rows the table lists.
export async function getBookingCounts(p: {
  when?: BookingWhen;
  from?: string;
  to?: string;
  q?: string;
  operatorId?: string;
  model?: string;
  tail?: string;
  airport?: string;
}) {
  const { data } = await api.get<BookingCounts>('/admin/bookings/counts', {
    params: p,
  });
  return data;
}
export async function getBooking(id: string) {
  const { data } = await api.get<AdminBookingDetail>(`/admin/bookings/${id}`);
  return data;
}
export async function updateBooking(
  id: string,
  // SHAR-1211 — the API's AdminUpdateBookingDto (aircraft reassignment, route,
  // FBOs and the ad-hoc label dismissals: SHAR-332/452/1088).
  body: BookingUpdateBody,
) {
  const { data } = await api.patch<AdminBookingDetail>(`/admin/bookings/${id}`, body);
  return data;
}

// ─── Airports lookup ───────────────────────────────────────────────────
export async function lookupAirports(q: string, limit = 10) {
  const { data } = await api.get<AirportHit[]>('/airports/lookup', { params: { q, limit } });
  return data;
}

// ─── Airports management (SHAR-204..210) ──────────────────────────────
export async function listAirports(p: {
  q?: string;
  /** SHAR-1192 — only the airports whose zone was guessed. */
  timezoneGuessed?: boolean;
  country?: string;
  status?: AirportStatus;
  page?: number;
  pageSize?: number;
}) {
  const { data } = await api.get<AirportPage>('/admin/airports', { params: p });
  return data;
}
export async function airportCountries() {
  const { data } = await api.get<{ code: string; name: string }[]>('/admin/airports/countries');
  return data;
}
export async function getAirport(id: string) {
  const { data } = await api.get<Airport>(`/admin/airports/${id}`);
  return data;
}
export async function createAirport(body: AirportCreateBody) {
  const { data } = await api.post<Airport>('/admin/airports', body);
  return data;
}
export async function updateAirport(id: string, body: AirportUpdateBody) {
  const { data } = await api.put<Airport>(`/admin/airports/${id}`, body);
  return data;
}
export async function deleteAirport(id: string) {
  await api.delete(`/admin/airports/${id}`);
}
export async function bulkAirportStatus(ids: string[], status: AirportStatus) {
  const { data } = await api.patch<{ updated: number }>('/admin/airports/bulk-status', {
    ids,
    status,
  });
  return data;
}

// ─── SHAR-106 — admin cancels a specific user's participation (release pre-auth) ──
export async function cancelBookingUser(bookingId: string, userId: string) {
  const { data } = await api.post<AdminBookingDetail>(
    `/admin/bookings/${bookingId}/cancel-user`,
    { userId },
  );
  return data;
}

// ─── SHAR-48 — admin passenger CRUD on a booking ──────────────────────
/** SHAR-108 — customers for the "Add user" dropdown (active only, at most 20). */
export async function searchAdminUsers(q: string) {
  const { data } = await api.get<{ id: string; email: string; name: string | null }[]>(
    '/admin/bookings/users/search',
    { params: { q } },
  );
  return data;
}
/** SHAR-108 — add an existing customer to a booking with N seats. */
export async function addUserToBooking(bookingId: string, userId: string, seats: number) {
  const { data } = await api.post<AdminBookingDetail & { alreadyOnBooking: boolean; addedSeats: number }>(
    `/admin/bookings/${bookingId}/users`,
    { userId, seats },
  );
  return data;
}
export async function addAdminPassenger(bookingId: string, body: BookingPassengerAddBody) {
  const { data } = await api.post<AdminBookingDetail>(
    `/admin/bookings/${bookingId}/passengers`,
    body,
  );
  return data;
}
export async function updateAdminPassenger(
  bookingId: string,
  passengerId: string,
  body: BookingPassengerUpdateBody,
) {
  const { data } = await api.patch<AdminBookingDetail>(
    `/admin/bookings/${bookingId}/passengers/${passengerId}`,
    body,
  );
  return data;
}
export async function deleteAdminPassenger(bookingId: string, passengerId: string) {
  const { data } = await api.delete<AdminBookingDetail>(
    `/admin/bookings/${bookingId}/passengers/${passengerId}`,
  );
  return data;
}

// ─── SHAR-320 — download a booking's calculation snapshot (.txt) ───────
export async function downloadCalculation(bookingId: string) {
  const res = await api.get(`/admin/bookings/${bookingId}/calculation`, {
    responseType: 'blob',
  });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const m = cd.match(/filename="(.+)"/);
  const filename = m?.[1] ?? `${bookingId}.txt`;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── SHAR-414 — aircraft assignable to an existing booking (free on its date) ─
export type AssignableAircraft = {
  id: string;
  modelName: string;
  tailNumber: string | null;
  category: string;
  seatingCapacity: number;
  operatorId: string;
};
export async function assignableAircraft(bookingId: string, operatorId?: string) {
  const { data } = await api.get<AssignableAircraft[]>(
    `/admin/bookings/${bookingId}/assignable-aircraft`,
    { params: { operatorId } },
  );
  return data;
}

// ─── Charters admin (SHAR Block 4) ────────────────────────────────────
export async function listCharters(p: {
  status?: CharterRequestStatus;
  // SHAR-872 — free-text search (request id, user, route, flight id).
  q?: string;
  page?: number;
  pageSize?: number;
  // SHAR-579 — Upcoming / Past.
  when?: 'upcoming' | 'past';
  // SHAR-110 — departure date range, inclusive, "yyyy-mm-dd".
  from?: string;
  to?: string;
  /** SHAR-1105 — only requests carrying the "Payment required" label. */
  paymentRequired?: boolean;
  /** SHAR-1116 — only requests carrying the "Quote refresh" label. */
  quoteRefresh?: boolean;
}) {
  const { data } = await api.get<CharterRequestList>('/admin/charters', {
    params: {
      ...p,
      paymentRequired: p.paymentRequired ? 'true' : undefined,
      quoteRefresh: p.quoteRefresh ? 'true' : undefined,
    },
  });
  return data;
}
/** SHAR-1105 — remove the "Payment required" ad-hoc label (admin panel only). */
export async function dismissCharterRequestPaymentRequired(id: string) {
  const { data } = await api.patch<CharterRequestDetail>(`/admin/charters/requests/${id}`, {
    dismissPaymentRequired: true,
  } satisfies CharterRequestUpdateBody);
  return data;
}
/** SHAR-1116 — remove the "Quote refresh" ad-hoc label. */
export async function dismissCharterRequestQuoteRefresh(id: string) {
  const { data } = await api.patch<CharterRequestDetail>(`/admin/charters/requests/${id}`, {
    dismissQuoteRefresh: true,
  } satisfies CharterRequestUpdateBody);
  return data;
}
export async function getCharter(id: string) {
  const { data } = await api.get<CharterRequestDetail>(`/admin/charters/${id}`);
  return data;
}
// SHAR-604 — live pricing preview for the quote form. Writes nothing; it runs
// the same calculation the save path uses, so the admin cannot be shown one
// total and the user charged another.
export async function previewCharterQuote(body: CharterQuotePreviewBody) {
  const { data } = await api.post<CharterQuoteBreakdown>('/admin/charters/quote-preview', body);
  return data;
}

export async function createCharterQuote(requestId: string, body: CharterQuoteBody) {
  const { data } = await api.post<CharterRequestDetail>(
    `/admin/charters/${requestId}/quotes`,
    body,
  );
  return data;
}
export async function updateCharterQuote(quoteId: string, body: CharterQuoteBody) {
  const { data } = await api.patch<CharterRequestDetail>(
    `/admin/charters/quotes/${quoteId}`,
    body,
  );
  return data;
}
export async function deleteCharterQuote(quoteId: string) {
  const { data } = await api.delete<CharterRequestDetail>(
    `/admin/charters/quotes/${quoteId}`,
  );
  return data;
}
export async function sendCharterQuotes(requestId: string) {
  const { data } = await api.post<CharterRequestDetail>(
    `/admin/charters/${requestId}/send-quotes`,
  );
  return data;
}

// SHAR-116 — cancel a charter request. Returns the refreshed request so the
// page never has to guess what the cancellation did to the quotes.
export async function cancelCharterRequest(requestId: string) {
  const { data } = await api.post<CharterRequestDetail>(
    `/admin/charters/requests/${requestId}/cancel`,
  );
  return data;
}

// SHAR-124 — cancel a charter BOOKING. The request is cancelled with it and the
// customer is told; the money is not touched.
export async function cancelCharterBooking(bookingId: string) {
  const { data } = await api.post<{ ok: boolean; alreadyCancelled?: boolean }>(
    `/admin/charters/bookings/${bookingId}/cancel`,
  );
  return data;
}

// ─── Charter bookings (SHAR-117/118/120/122/123) ──────────────────────

export async function listCharterBookings(p: {
  status?: CharterBookingStatus;
  // SHAR-873 — free-text search (booking id, user, route, flight id).
  q?: string;
  when?: 'upcoming' | 'past';
  page?: number;
  pageSize?: number;
  // SHAR-119 — departure date range, inclusive, "yyyy-mm-dd".
  from?: string;
  to?: string;
  /** SHAR-1027 — only bookings carrying the "Passport needed" label. */
  passportNeeded?: boolean;
  /** SHAR-131 — only bookings carrying the "Wire transfer required" label. */
  wireTransfer?: boolean;
}) {
  const { data } = await api.get<CharterBookingList>('/admin/charters/bookings', {
    params: {
      ...p,
      passportNeeded: p.passportNeeded ? 'true' : undefined,
      wireTransfer: p.wireTransfer ? 'true' : undefined,
    },
  });
  return data;
}
export async function getCharterBooking(id: string) {
  const { data } = await api.get<CharterBookingDetail>(`/admin/charters/bookings/${id}`);
  return data;
}
// SHAR-120/130 — flight number, FBO overrides, status, wire-transfer receipt.
export async function updateCharterBooking(
  id: string,
  // SHAR-1211 — the API's AdminUpdateCharterBookingDto: flight number, status,
  // wire receipt, label dismissals, FBOs, aircraft (SHAR-738/766) and itinerary
  // (SHAR-735) corrections; null clears an override.
  body: CharterBookingUpdateBody,
) {
  const { data } = await api.patch<CharterBookingDetail>(`/admin/charters/bookings/${id}`, body);
  return data;
}
// SHAR-122 — the manifest is editable from the admin side too.
export async function addCharterPassenger(bookingId: string, body: CharterPassengerAddBody) {
  const { data } = await api.post<CharterBookingDetail>(
    `/admin/charters/bookings/${bookingId}/passengers`,
    body,
  );
  return data;
}
export async function updateCharterPassenger(
  passengerId: string,
  body: CharterPassengerUpdateBody,
) {
  const { data } = await api.patch<CharterBookingDetail>(
    `/admin/charters/passengers/${passengerId}`,
    body,
  );
  return data;
}
export async function removeCharterPassenger(passengerId: string) {
  const { data } = await api.delete<CharterBookingDetail>(
    `/admin/charters/passengers/${passengerId}`,
  );
  return data;
}

// ─── Platform settings (SHAR-321/322/318) ─────────────────────────────
export async function getSettings() {
  const { data } = await api.get<AppSettings>('/admin/settings');
  return data;
}
export async function updateSettings(body: Partial<AppSettings>) {
  const { data } = await api.put<AppSettings>('/admin/settings', body);
  return data;
}

// ─── File upload ───────────────────────────────────────────────────────
/**
 * Copilot review — `kind: 'passenger-doc'` files the upload under the guarded
 * passenger-document prefix instead of the public aircraft one.
 *
 * The server chooses the prefix from who is authenticated, which is right for a
 * customer but wrong here: an admin uploading a passenger's passport from the
 * booking or charter screen was filing it among the aircraft photographs, where
 * the redirect route is deliberately public. Aircraft photographs keep the
 * default; anything belonging to a passenger says so.
 */
export async function uploadFile(
  file: File,
  kind?: 'passenger-doc',
): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  if (kind) fd.append('kind', kind);
  const { data } = await api.post<{ url: string }>('/files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
